/**
 * Neck-label content + print SVG for the product editor.
 *
 * Legal copy (size / fabric / origin / care) is derived from the blank's
 * catalog data — merchants restyle it, they do not rewrite it.
 */

import { inferLocationPresetKey } from "../constants/categoryDefaultLocations";
import { placementHasRealArt } from "./placementArt";
import { wrapLabelText } from "./labelText";

export const NECK_LABEL_PLACEMENT_ID = "5";
export const BACK_PRINT_PLACEMENT_ID = "2";

export const SIZE_FONT_STYLES = [
  {
    id: "block",
    label: "Bold block",
    preview: "M",
    fontFamily: '"Arial Black", "Helvetica Neue", Impact, sans-serif',
    fontWeight: 900,
    letterSpacing: "-0.04em",
    fontSize: 118,
  },
  {
    id: "serif",
    label: "Classic serif",
    preview: "M",
    // Times first: Georgia only has old-style digits, so the "3" in "3XL"
    // dropped below the baseline into the fabric line.
    fontFamily: '"Times New Roman", Times, Georgia, serif',
    fontWeight: 700,
    letterSpacing: "0",
    fontSize: 108,
    numeric: "lining-nums",
    descent: 0.1,
  },
  {
    id: "condensed",
    label: "Condensed",
    preview: "M",
    fontFamily: '"Arial Narrow", "Helvetica Neue", Arial, sans-serif',
    fontWeight: 700,
    letterSpacing: "0.08em",
    fontSize: 112,
  },
];

export const CARE_LAYOUTS = [
  {
    id: "symbols",
    label: "Laundry symbols",
    description: "Standard care icons in a row",
  },
  {
    id: "text",
    label: "Written care",
    description: "Care line under the size",
  },
];

export const DEFAULT_APPAREL_CARE_TEXT =
  "Machine wash cold with like colours. Do not bleach. Tumble dry low. Cool iron. Do not dry clean.";

export const DEFAULT_NECK_LABEL_CONFIG = {
  mode: "inner",
  artworkSource: "generated",
  sizeFontStyle: "block",
  careLayout: "both",
  careSymbols: true,
  careWritten: true,
  textColor: "auto",
  logoSrc: "",
  logoName: "",
  sizeArt: {},
  unmatchedSizeArt: [],
  excludedSizes: [],
};

/** Letter-size aliases. Longer phrases are matched before single letters. */
const SIZE_RANK_ALIASES = [
  ["XXS", "2XS"],
  ["XS", "EXTRA SMALL", "X-SMALL", "X SMALL", "XSMALL"],
  ["S", "SMALL", "SML"],
  ["M", "MEDIUM", "MED"],
  ["L", "LARGE", "LRG"],
  ["XL", "X-LARGE", "X LARGE", "XLARGE", "EXTRA LARGE", "XLG"],
  ["2XL", "2X-LARGE", "2X LARGE", "2XLARGE", "XX-LARGE", "XX LARGE", "XXL", "2X"],
  ["3XL", "3X-LARGE", "3X LARGE", "3XLARGE", "XXX-LARGE", "XXX LARGE", "XXXL", "3X"],
  ["4XL", "4X-LARGE", "4X LARGE", "4XLARGE", "XXXXL", "4X"],
  ["5XL", "5X-LARGE", "5X LARGE", "5XLARGE", "XXXXXL", "5X"],
  ["6XL", "6X-LARGE", "6X LARGE", "6XLARGE", "6X"],
  ["OS", "ONE SIZE", "ONESIZE", "ONE-SIZE", "OSFA"],
];

const YOUTH_ALIASES = {
  YXS: ["yxs", "youth xs", "youth x-small", "youth extra small"],
  YS: ["ys", "youth s", "youth small"],
  YM: ["ym", "youth m", "youth medium"],
  YL: ["yl", "youth l", "youth large"],
  YXL: ["yxl", "youth xl", "youth x-large"],
};



export const NECK_LABEL_ART_SIZE = { width: 720, height: 480 };
// Uploaded logo fills the space from the top of the label down to the top
// of the size. Values are in NECK_LABEL_ART_SIZE units.
export const NECK_LABEL_LOGO_PAD = { top: 12, side: 60, gap: 14 };
// Where the bottom of the care copy lands inside the 480-tall label art.
export const NECK_LABEL_CONTENT_BOTTOM = 468;

/** Named presets matching Printify's font-color dropdown (Auto + greys). */
export const NECK_LABEL_COLOR_PRESETS = [
  { id: "auto", label: "Auto", hex: null },
  { id: "white", label: "White", hex: "#ffffff" },
  { id: "light-grey", label: "Light grey", hex: "#d0d5dd" },
  { id: "dark-grey", label: "Dark grey", hex: "#475467" },
  { id: "black", label: "Black", hex: "#111111" },
];

/** Neutral row + Printify-style color grid, then a custom hex field in the UI. */
export const TEXT_COLOR_SWATCHES = [
  "#ffffff",
  "#e4e7ec",
  "#98a2b3",
  "#475467",
  "#111111",
  "#f04438",
  "#f79009",
  "#fac515",
  "#12b76a",
  "#15b79e",
  "#0ba5ec",
  "#2e90fa",
  "#6172f3",
  "#7a5af8",
  "#ee46bc",
  "#f63d68",
  "#d92d20",
  "#dc6803",
  "#eaaa08",
  "#039855",
  "#0e9384",
  "#026aa2",
  "#1570ef",
  "#444ce7",
  "#5925dc",
  "#dd2590",
];

export function normalizeHexColor(value) {
  const raw = String(value || "").trim();
  if (!raw || raw === "auto") return "";
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  const hex = withHash.replace(/[^#a-fA-F0-9]/g, "");
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase();
  return "";
}

export function hexLuminance(hex) {
  const n = normalizeHexColor(hex).replace("#", "");
  if (n.length < 6) return 1;
  const r = parseInt(n.slice(0, 2), 16) / 255;
  const g = parseInt(n.slice(2, 4), 16) / 255;
  const b = parseInt(n.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function resolveNeckLabelTextColor(config, garmentHex) {
  const raw = config?.textColor;
  if (!raw || raw === "auto") {
    return hexLuminance(garmentHex) > 0.55 ? "#111111" : "#ffffff";
  }
  const preset = NECK_LABEL_COLOR_PRESETS.find((item) => item.id === raw);
  if (preset?.hex) return preset.hex;
  return normalizeHexColor(raw) || "#111111";
}

export function neckLabelColorPresetId(textColor) {
  if (!textColor || textColor === "auto") return "auto";
  const normalized = normalizeHexColor(textColor);
  const byId = NECK_LABEL_COLOR_PRESETS.find((item) => item.id === textColor);
  if (byId) return byId.id;
  const byHex = NECK_LABEL_COLOR_PRESETS.find(
    (item) => item.hex && item.hex.toLowerCase() === normalized,
  );
  return byHex?.id || "custom";
}

function careFlagsFromLayout(layout) {
  if (layout === "none") return { careSymbols: false, careWritten: false };
  if (layout === "text") return { careSymbols: false, careWritten: true };
  if (layout === "symbols") return { careSymbols: true, careWritten: false };
  return { careSymbols: true, careWritten: true };
}

/** Laundry symbols and written care are independent. Both can be on. */
export function careFlagsFromConfig(config = {}) {
  const raw = config || {};
  const hasSymbols = Object.prototype.hasOwnProperty.call(raw, "careSymbols");
  const hasWritten = Object.prototype.hasOwnProperty.call(raw, "careWritten");
  if (hasSymbols || hasWritten) {
    const fromLayout = careFlagsFromLayout(raw.careLayout);
    return {
      careSymbols: hasSymbols ? !!raw.careSymbols : fromLayout.careSymbols,
      careWritten: hasWritten ? !!raw.careWritten : fromLayout.careWritten,
    };
  }
  return careFlagsFromLayout(raw.careLayout);
}

export function careLayoutFromFlags(careSymbols, careWritten) {
  if (careSymbols && careWritten) return "both";
  if (careSymbols) return "symbols";
  if (careWritten) return "text";
  return "none";
}

const MADE_IN_RE = /made\s+in\s+([a-z][a-z\s.'-]+)/gi;
const ORIGIN_TITLE_RE = /\b(origin|made in|country)\b/i;
const FABRIC_TITLE_RE = /\b(fabric|cotton|polyester|blend|material)\b/i;
const ORIGIN_NOISE_RE =
  /\b(facilit(?:y|ies)|certified|certification|oeko-?tex|gots|fla|wrap|bsci|sedex|responsible supplier|compliance)\b/i;
const BLEND_RE =
  /\b(polyester|poly\b|viscose|rayon|elastane|spandex|nylon|modal|blend|\d+\s*\/\s*\d+)\b/i;
const FABRIC_LINE_RE =
  /(\d{1,3}(?:\.\d+)?\s*%\s*(?:organic\s+|combed\s+|carded\s+|ringspun\s+)?(?:cotton|polyester|poly|viscose|rayon|elastane|spandex|nylon|modal|linen|wool|acrylic)(?:\s*(?:\/|,|and)\s*\d{1,3}(?:\.\d+)?\s*%\s*[a-z]+)*)/i;
const FABRIC_RATIO_RE =
  /(\d{1,3}\s*\/\s*\d{1,3}\s+(?:cotton[-\s]?poly(?:ester)?|poly[-\s]?cotton|cotton|polyester))/i;
const CARE_LINE_RE =
  /((?:machine\s+)?wash(?:\s+(?:cold|warm|hot|inside\s+out|gentle|delicate))?[^.;\n]{0,80}|tumble\s+dry[^.;\n]{0,60}|line\s+dry[^.;\n]{0,40}|do\s+not\s+(?:bleach|tumble\s+dry|dry\s*clean|iron)[^.;\n]{0,40}|(?:cool|warm)\s+iron[^.;\n]{0,40})/gi;

const COUNTRY_CODES = {
  us: "USA",
  usa: "USA",
  au: "Australia",
  ca: "Canada",
  cn: "China",
  vn: "Vietnam",
  bd: "Bangladesh",
  in: "India",
  pk: "Pakistan",
  kh: "Cambodia",
  hn: "Honduras",
  ni: "Nicaragua",
  ht: "Haiti",
  do: "Dominican Republic",
  gt: "Guatemala",
  mx: "Mexico",
  sv: "El Salvador",
  pe: "Peru",
  co: "Colombia",
  pt: "Portugal",
  tr: "Turkey",
  eg: "Egypt",
  jo: "Jordan",
  lk: "Sri Lanka",
  id: "Indonesia",
  th: "Thailand",
  my: "Malaysia",
  ph: "Philippines",
  ke: "Kenya",
  et: "Ethiopia",
};

export function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function nonemptyText(value) {
  if (value == null) return "";
  if (typeof value === "object") return "";
  const text = String(value).replace(/\s+/g, " ").trim();
  return text === "[object Object]" ? "" : text;
}

export function coerceSizeLabel(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return nonemptyText(
      value.size || value.label || value.name || value.code || value.id,
    );
  }
  return nonemptyText(value);
}

function uniqueStrings(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((value) => {
    const text = nonemptyText(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  });
  return out;
}

function uniqueSizeLabels(values) {
  return uniqueStrings((values || []).map(coerceSizeLabel));
}

export function stripHtmlText(value) {
  return nonemptyText(
    String(value || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|li|div|h\d)>/gi, "\n")
      .replace(/<[^>]+>/g, " "),
  );
}

function catalogBlob(blankProduct) {
  return [
    blankProduct?.about,
    blankProduct?.settings?.about,
    blankProduct?.description,
    blankProduct?.settings?.description,
    blankProduct?.material,
    blankProduct?.fabric,
    blankProduct?.careInstructions,
    blankProduct?.settings?.careInstructions,
  ]
    .map((value) =>
      typeof value === "string" ? stripHtmlText(value) : nonemptyText(value),
    )
    .filter(Boolean)
    .join("\n");
}

const COUNTRY_NAME_LOOKUP = Object.values(COUNTRY_CODES).reduce((acc, name) => {
  acc[name.toLowerCase()] = name;
  return acc;
}, {
  "united states": "USA",
  "united states of america": "USA",
  america: "USA",
});

const LABEL_COUNTRY_PRIORITY = [
  "Honduras",
  "Nicaragua",
  "Haiti",
  "Dominican Republic",
  "Bangladesh",
  "China",
  "Vietnam",
];

function countryNameFromToken(token) {
  const raw = nonemptyText(token).toLowerCase().replace(/[.,;:]+$/, "");
  if (!raw || ORIGIN_NOISE_RE.test(raw)) return "";
  if (COUNTRY_CODES[raw]) return COUNTRY_CODES[raw];
  if (COUNTRY_NAME_LOOKUP[raw]) return COUNTRY_NAME_LOOKUP[raw];
  const hit = Object.keys(COUNTRY_NAME_LOOKUP).find(
    (name) => raw === name || raw.startsWith(`${name} `),
  );
  return hit ? COUNTRY_NAME_LOOKUP[hit] : "";
}

function countriesFromValue(raw) {
  const text = nonemptyText(raw);
  if (!text || ORIGIN_NOISE_RE.test(text)) return [];
  return uniqueStrings(
    text
      .split(/[,/;|]+/)
      .map((part) => countryNameFromToken(part))
      .filter(Boolean),
  );
}

function pickLabelCountry(names) {
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return LABEL_COUNTRY_PRIORITY.find((name) => names.includes(name)) || names[0];
}

function formatOriginValue(raw) {
  const names = countriesFromValue(raw);
  if (names.length) return `Made in ${pickLabelCountry(names)}`;
  const text = nonemptyText(raw);
  if (!text || ORIGIN_NOISE_RE.test(text)) return "";
  const matches = String(text).matchAll(MADE_IN_RE);
  for (const match of matches) {
    const country = countryNameFromToken(match[1]);
    if (country) return `Made in ${country}`;
  }
  return "";
}

function tidyFiber(value) {
  return nonemptyText(value).replace(
    /\b(cotton|polyester|poly|viscose|rayon|elastane|spandex|nylon|modal|linen|wool|acrylic|combed|carded|ringspun|organic)\b/gi,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );
}

function compositionFromText(text) {
  const blob = stripHtmlText(text);
  if (!blob) return "";
  const line = blob.match(FABRIC_LINE_RE)?.[1];
  if (line) return tidyFiber(line);
  const ratio = blob.match(FABRIC_RATIO_RE)?.[1];
  if (ratio) return tidyFiber(ratio);
  return "";
}

function inferCottonOnly(text) {
  const blob = stripHtmlText(text);
  if (!blob || !/cotton/i.test(blob) || BLEND_RE.test(blob)) return "";
  if (
    /ring[-\s]?spun cotton|combed cotton|carded cotton|100%\s*cotton|cotton jersey|garment-?dyed/i.test(
      blob,
    )
  ) {
    return "100% Cotton";
  }
  return "";
}

export function parseFabricFromText(text) {
  return compositionFromText(text) || inferCottonOnly(text);
}

export function parseCareFromText(text) {
  const blob = stripHtmlText(text);
  if (!blob) return "";
  const hits = [];
  blob.replace(CARE_LINE_RE, (match) => {
    const cleaned = nonemptyText(match.replace(/[.,;:]+$/, ""));
    if (cleaned && cleaned.length < 90) hits.push(cleaned);
    return match;
  });
  return uniqueStrings(hits).join("; ");
}

export function inferCareSteps(careText) {
  const blob = String(careText || "").toLowerCase();
  if (!blob) return [];
  const steps = [];
  const add = (icon, label) => {
    if (steps.some((step) => step.icon === icon)) return;
    steps.push({ icon, label });
  };
  if (/do not bleach|no bleach/.test(blob)) add("no_bleach", "Do not bleach");
  if (/do not dry\s*clean|no dry clean/.test(blob)) {
    add("no_dryclean", "Do not dry clean");
  } else if (/dry clean/.test(blob)) {
    add("dryclean", "Dry clean");
  }
  if (/do not tumble|line dry|hang dry|air dry/.test(blob)) {
    add("tumble_none", "Do not tumble dry");
  } else if (/tumble dry/.test(blob)) {
    add("tumble_low", "Tumble dry low");
  }
  if (/do not iron|do not iron print/.test(blob)) {
    add("iron_none", "Do not iron print");
  } else if (/iron/.test(blob)) {
    add("iron_low", "Cool iron");
  }
  if (/wash/.test(blob)) {
    add(
      /warm|hot|40/.test(blob) ? "wash_warm" : "wash_cold",
      /warm|hot/.test(blob) ? "Machine wash warm" : "Machine wash cold",
    );
  }
  return steps;
}

function defaultApparelCare(fabric) {
  const blob = String(fabric || "").toLowerCase();
  if (!blob) return { text: "", steps: [] };
  if (!/(cotton|poly|blend|viscose|fleece|garment)/.test(blob)) {
    return { text: "", steps: [] };
  }
  const text = DEFAULT_APPAREL_CARE_TEXT;
  return { text, steps: inferCareSteps(text) };
}

function featureText(feature) {
  if (!feature) return "";
  if (typeof feature === "string") return nonemptyText(feature);
  return nonemptyText(
    feature.body || feature.description || feature.text || feature.title,
  );
}

function featureTitle(feature) {
  if (!feature || typeof feature !== "object") return "";
  return nonemptyText(feature.title || feature.name || feature.label);
}

function collectKeyFeatures(blankProduct) {
  const raw =
    blankProduct?.keyFeatures ||
    blankProduct?.settings?.keyFeatures ||
    [];
  return Array.isArray(raw) ? raw : [];
}

function extractOrigin(blankProduct, features) {
  const direct = formatOriginValue(
    blankProduct?.countryOfOrigin ||
      blankProduct?.country_of_origin ||
      blankProduct?.madeIn ||
      blankProduct?.origin ||
      blankProduct?.settings?.countryOfOrigin,
  );
  if (direct) return direct;

  const originFeature = features.find((feature) => {
    const icon = String(feature?.icon || "").toLowerCase();
    const title = featureTitle(feature);
    return icon === "origin" || ORIGIN_TITLE_RE.test(title);
  });
  const fromFeature = formatOriginValue(featureText(originFeature));
  if (fromFeature) return fromFeature;

  const fromBlob = formatOriginValue(catalogBlob(blankProduct));
  if (fromBlob) return fromBlob;
  return (
    asColourPublishedOrigin(blankProduct) || ssPublishedOrigin(blankProduct)
  );
}

const AS_COLOUR_BANGLADESH_STYLES = new Set([
  "5001",
  "5001R",
  "5001M",
  "5001Y",
  "5073",
  "4001",
  "4017",
  "4033",
]);

/**
 * AS Colour's catalog API omits COO. Their US 5001 Care tab and Production
 * FAQ publish core tees as Made in Bangladesh.
 */
function asColourPublishedOrigin(blankProduct) {
  const id = String(blankProduct?.id || "");
  const brand = [blankProduct?.brand, blankProduct?.supplierName, id].join(" ");
  const isAsColour =
    /as\s*colour|ascolour/i.test(brand) || /^AS\d/i.test(id);
  if (!isAsColour) return "";
  const style = String(
    blankProduct?.style ||
      blankProduct?.blankProductId ||
      id.replace(/^AS(AU)?/i, "") ||
      "",
  )
    .trim()
    .toUpperCase()
    .replace(/^AS(AU)?/, "");
  const name = [blankProduct?.name, blankProduct?.productName].join(" ");
  if (AS_COLOUR_BANGLADESH_STYLES.has(style) || style.startsWith("5001")) {
    return "Made in Bangladesh";
  }
  if (
    /\b(staple|maple|classic)\b/i.test(name) &&
    /\b(tee|tank|singlet)\b/i.test(name)
  ) {
    return "Made in Bangladesh";
  }
  return "";
}

function catalogStyleCode(blankProduct) {
  const id = String(blankProduct?.id || "");
  return String(
    blankProduct?.style ||
      blankProduct?.blankProductId ||
      id.replace(/^(SS(CA)?|SANMAR(CA)?|CC|AS(AU)?)/i, "") ||
      "",
  )
    .trim()
    .toUpperCase()
    .replace(/^(SS(CA)?|SANMAR(CA)?|CC|AS(AU)?)/, "");
}

/**
 * Comfort Colors 1717 is Honduras. Used when S&S marketing copy has no COO
 * field and the origin feature is a certification blurb.
 */
function ssPublishedOrigin(blankProduct) {
  const id = String(blankProduct?.id || "");
  const brand = [blankProduct?.brand, blankProduct?.supplierName, id].join(" ");
  const isSS =
    /s\s*&\s*s|ss activewear|comfort colours?|^SS\d/i.test(brand) ||
    /^SS/i.test(id);
  if (!isSS) return "";
  const style = catalogStyleCode(blankProduct);
  if (style === "1717" || style.startsWith("1717")) return "Made in Honduras";
  return "";
}

function extractFabric(blankProduct, features) {
  const fabricFeature = features.find((feature) => {
    const icon = String(feature?.icon || "").toLowerCase();
    const title = featureTitle(feature);
    return icon === "cotton" || icon === "fabric" || FABRIC_TITLE_RE.test(title);
  });
  const sources = [
    blankProduct?.material,
    blankProduct?.fabric,
    blankProduct?.settings?.material,
    blankProduct?.settings?.fabric,
    featureText(fabricFeature),
    catalogBlob(blankProduct),
  ];
  for (const source of sources) {
    const composition = compositionFromText(source);
    if (composition) return composition;
  }
  for (const source of sources) {
    const cotton = inferCottonOnly(source);
    if (cotton) return cotton;
  }
  return "";
}

function extractCare(blankProduct, fabric = "") {
  const raw =
    blankProduct?.careInstructions ||
    blankProduct?.settings?.careInstructions ||
    blankProduct?.care ||
    null;

  let text = "";
  let steps = [];
  if (typeof raw === "string") {
    text = stripHtmlText(raw);
  } else if (raw && typeof raw === "object") {
    steps = Array.isArray(raw.steps)
      ? raw.steps
          .map((step) => {
            if (!step) return null;
            if (typeof step === "string") {
              return { icon: "wash_cold", label: nonemptyText(step) };
            }
            const label = nonemptyText(step.label || step.text || step.name);
            const icon = nonemptyText(step.icon) || "wash_cold";
            if (!label && !icon) return null;
            return { icon, label };
          })
          .filter(Boolean)
      : [];
    text = nonemptyText(raw.text) || steps.map((step) => step.label).join("; ");
  }

  if (!text) text = parseCareFromText(catalogBlob(blankProduct));
  if (!steps.length) steps = inferCareSteps(text);
  if (!text && !steps.length) return defaultApparelCare(fabric);
  return { text, steps };
}

const APPAREL_SIZE_ORDER = [
  "XXS", "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL",
];
const APPAREL_SIZE_ALIASES = { XXL: "2XL", XXXL: "3XL", XXXXL: "4XL", XXXXXL: "5XL" };

function apparelSizeRank(size) {
  const key = String(size || "").trim().toUpperCase();
  const canon = APPAREL_SIZE_ALIASES[key] || key;
  const idx = APPAREL_SIZE_ORDER.indexOf(canon);
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
}

/**
 * Put sizes in the blank's own order (its settings list), falling back to
 * standard apparel order. Saved products store sizes as map keys, so they
 * came back alphabetical (2XL, 3XL, … XS) and previewed 2XL.
 */
export function orderSizesLikeBlank(sizes, blankSizes = []) {
  const blankOrder = uniqueSizeLabels(blankSizes || []).map((size) =>
    String(size).trim().toUpperCase(),
  );
  // Standard apparel sizes always read XS → 6XL, whatever order the blank's
  // list happens to be in. Anything else (youth, one-size, custom) follows
  // the blank's list, then stays in its incoming order.
  const rank = (size) => {
    const apparel = apparelSizeRank(size);
    if (Number.isFinite(apparel)) return apparel;
    const idx = blankOrder.indexOf(String(size).trim().toUpperCase());
    return idx === -1 ? 10000 : 100 + idx;
  };
  return [...(sizes || [])]
    .map((size, i) => ({ size, i }))
    .sort((a, b) => rank(a.size) - rank(b.size) || a.i - b.i)
    .map(({ size }) => size);
}

function extractSizes(blankProduct, selectedSizes = []) {
  const fromSelected = uniqueSizeLabels(selectedSizes);
  if (fromSelected.length) {
    return orderSizesLikeBlank(
      fromSelected,
      blankProduct?.settings?.sizes ||
        (blankProduct?.blankVariants || []).map((variant) => variant?.size),
    );
  }

  const fromSettings = uniqueSizeLabels(blankProduct?.settings?.sizes || []);
  if (fromSettings.length) return fromSettings;

  return uniqueSizeLabels(
    (blankProduct?.blankVariants || []).map((variant) => variant?.size),
  );
}

/**
 * Flatten catalog copy that may live on the product or its settings doc
 * so blank-settings previews see the same care/fabric/origin as the PDP.
 */
export function composeBlankForNeckLabel(blankProduct, extras = {}) {
  const settings = blankProduct?.settings || {};
  const selectedSizes = extras.selectedSizes || [];
  return {
    ...blankProduct,
    material: settings.material || blankProduct?.material,
    fabric: settings.fabric || blankProduct?.fabric,
    countryOfOrigin:
      settings.countryOfOrigin ||
      blankProduct?.countryOfOrigin ||
      settings.madeIn ||
      blankProduct?.madeIn,
    careInstructions:
      settings.careInstructions ||
      blankProduct?.careInstructions ||
      settings.care ||
      blankProduct?.care,
    keyFeatures: blankProduct?.keyFeatures || settings.keyFeatures,
    about: blankProduct?.about || settings.about,
    description: blankProduct?.description || settings.description,
    settings: {
      ...settings,
      // Saved products hand back their sizes as map keys (alphabetical).
      // Keep the blank's own order so the list and preview start at XS.
      sizes: selectedSizes.length
        ? orderSizesLikeBlank(
            selectedSizes,
            settings.sizes ||
              (extras.blankVariants || blankProduct?.blankVariants || []).map(
                (variant) => variant?.size,
              ),
          )
        : settings.sizes || blankProduct?.settings?.sizes,
    },
    blankVariants: extras.blankVariants || blankProduct?.blankVariants,
  };
}

/**
 * Pull the legally required inner-label fields from a blank product.
 */
export function extractNeckLabelContent(blankProduct, options = {}) {
  const features = collectKeyFeatures(blankProduct);
  const sizes = extractSizes(blankProduct, options.selectedSizes);
  const previewSize =
    coerceSizeLabel(options.previewSize) || sizes[0] || "";
  const fabric = extractFabric(blankProduct, features);
  const origin = extractOrigin(blankProduct, features);
  const care = extractCare(blankProduct, fabric);

  return {
    sizes,
    previewSize,
    fabric,
    origin,
    careText: care.text,
    careSteps: care.steps,
    hasRequiredCopy: Boolean(fabric || origin || care.text || care.steps.length),
  };
}

export function getSizeFontStyle(styleId) {
  return (
    SIZE_FONT_STYLES.find((style) => style.id === styleId) || SIZE_FONT_STYLES[0]
  );
}

export function isApparelEligibleForNeckLabel(blankProduct) {
  if (!blankProduct || typeof blankProduct !== "object") return false;
  const preset = inferLocationPresetKey({
    name: blankProduct.name || blankProduct.productName,
    productName: blankProduct.productName,
    style: blankProduct.style,
    brand: blankProduct.brand,
    category: blankProduct.category,
    shippingCategory: blankProduct.shippingCategory,
    categories: blankProduct.categories || blankProduct.productCategories,
    presetKey: blankProduct.presetKey || blankProduct.settings?.presetKey,
  });
  // Master placement ids short-circuit inferLocationPresetKey to "catalog".
  // Still treat tees/sweats as eligible from the name/category blob.
  if (preset === "headwear" || preset === "drinkware") return false;
  if (preset === "apparel") return true;

  const blob = [
    blankProduct.name,
    blankProduct.productName,
    blankProduct.style,
    blankProduct.category,
    blankProduct.shippingCategory,
    ...(blankProduct.categories || []),
  ]
    .map((value) => (typeof value === "string" ? value : value?.name || ""))
    .join(" ");
  return /\b(tee|t-?shirt|hoodie|sweat|polo|tank|shirt|garment|apparel|fleece)\b/i.test(
    blob,
  );
}

/** True when placement 5 is turned on (admin Blank Settings → Placements). */
export function placementsIncludeNeckLabel(placements) {
  if (!placements || typeof placements !== "object" || Array.isArray(placements)) {
    return false;
  }
  const entry =
    placements[NECK_LABEL_PLACEMENT_ID] ?? placements[Number(NECK_LABEL_PLACEMENT_ID)];
  if (entry == null || entry === false) return false;
  if (typeof entry === "object" && entry.active === false) return false;
  return true;
}

function shopPlacementMap(blankProduct) {
  const candidates = [
    blankProduct?.settings?.placements,
    blankProduct?.blankSettings?.placements,
  ];
  return candidates.find(
    (placements) =>
      placements &&
      typeof placements === "object" &&
      !Array.isArray(placements),
  );
}

export function blankHasNeckLabelPlacement(blankProduct) {
  // The enabled-blank payload spreads the catalog master first, so
  // `placements` still lists every catalog side (including neck label)
  // after the shop turns it off. The shop map is the placements page.
  const shop = shopPlacementMap(blankProduct);
  if (shop) return placementsIncludeNeckLabel(shop);
  return placementsIncludeNeckLabel(blankProduct?.placements);
}

export function canIntroduceNeckLabel({
  blankProduct,
  printPlacements = [],
  activePlacements = {},
  hideProductSettings = false,
} = {}) {
  if (hideProductSettings) return false;
  if (!activePlacements?.[NECK_LABEL_PLACEMENT_ID]) return false;
  if ((printPlacements || []).map(String).includes(NECK_LABEL_PLACEMENT_ID)) {
    return false;
  }
  return blankHasNeckLabelPlacement(blankProduct);
}

export function hasBackPrintArt(placementDetails, colorKey = "default") {
  const tree =
    placementDetails?.[colorKey] || placementDetails?.default || {};
  return placementHasRealArt(tree?.[BACK_PRINT_PLACEMENT_ID]);
}

export function findNeckLabelObjectIds(selectedObjects = {}) {
  return Object.entries(selectedObjects || {})
    .filter(([, object]) => object?.isNeckLabel || object?.isComplianceLabel)
    .map(([id]) => id);
}

function objectLooksLikeArt(object) {
  return Boolean(
    object && (object.url || object.svgString || object.file || object.printFile),
  );
}

/**
 * True when placement 5 already has merchant-uploaded art that is not the
 * generated inner/compliance label. Old products saved before this feature
 * must keep that artwork — we never stamp size/care on top of it.
 */
export function placementHasManualNeckArt(placement) {
  if (!placement || typeof placement !== "object") return false;
  const objects = Object.values(placement.selectedObjects || {});
  if (
    objects.some(
      (object) =>
        objectLooksLikeArt(object) &&
        !object.isNeckLabel &&
        !object.isComplianceLabel,
    )
  ) {
    return true;
  }
  return placementHasRealArt(placement) && !placement.neckLabel;
}

/**
 * Visiting the neck-label tab must not stamp printable art. Generated
 * size/care pixels are committed only by Apply or a logo upload.
 */
export function shouldAutoApplyGeneratedNeckLabel(_placement) {
  return false;
}

/**
 * When to write generated neck-label pixels onto the canvas / product.
 * Font/color tweaks stay draft until Apply, unless the placement is
 * already committed (keep the canvas in sync) or they uploaded a logo.
 */
export function shouldCommitGeneratedNeckLabel({
  explicitApply = false,
  addedLogo = false,
  alreadyCommitted = false,
} = {}) {
  return Boolean(explicitApply || addedLogo || alreadyCommitted);
}

function careSymbolMarkup(icon, x, y, color) {
  const stroke = `fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"`;
  const name = String(icon || "wash_cold");
  let inner = `<circle cx="20" cy="20" r="10" ${stroke}/>`;
  if (name === "wash_cold" || name === "wash_warm") {
    inner = `<rect x="8" y="12" width="24" height="18" rx="2" ${stroke}/>
      <path d="M12 12V9h16v3M14 22c2 2 4 2 6 0s4-2 6 0" ${stroke}/>
      <text x="20" y="20" text-anchor="middle" font-size="7" fill="${color}" font-family="sans-serif">${
        name === "wash_warm" ? "40" : "30"
      }</text>`;
  } else if (name === "no_bleach") {
    inner = `<path d="M20 8l12 22H8L20 8z" ${stroke}/>
      <path d="M12 28L28 12" ${stroke}/>`;
  } else if (name === "tumble_low" || name === "tumble_none") {
    inner = `<rect x="8" y="8" width="24" height="24" rx="2" ${stroke}/>
      <circle cx="20" cy="20" r="7" ${stroke}/>
      ${
        name === "tumble_none"
          ? `<path d="M12 28L28 12" ${stroke}/>`
          : `<circle cx="20" cy="20" r="1.5" fill="${color}"/>`
      }`;
  } else if (name === "iron_low" || name === "iron_none") {
    inner = `<path d="M8 24h20c2 0 4-2 4-4H14l-2-6H8v10z" ${stroke}/>
      ${
        name === "iron_none"
          ? `<path d="M10 30L30 10" ${stroke}/>`
          : `<circle cx="16" cy="28" r="1.2" fill="${color}"/>`
      }`;
  } else if (name === "no_dryclean" || name === "dryclean") {
    inner = `<circle cx="20" cy="20" r="10" ${stroke}/>
      <text x="20" y="24" text-anchor="middle" font-size="12" fill="${color}" font-family="sans-serif">P</text>
      ${name === "no_dryclean" ? `<path d="M10 30L30 10" ${stroke}/>` : ""}`;
  }
  return `<g transform="translate(${x} ${y})"><svg width="48" height="48" viewBox="0 0 40 40">${inner}</svg></g>`;
}

/**
 * Fit a generated neck label into the print box and pin it to the bottom.
 * DPI upscale is skipped by callers so the letter cannot overflow the box.
 */
export function neckLabelCanvasLayout({
  canvasWidth,
  canvasHeight,
  imageWidth,
  imageHeight,
} = {}) {
  const width = Number(canvasWidth) || 0;
  const height = Number(canvasHeight) || 0;
  const srcW = Number(imageWidth) || 1;
  const srcH = Number(imageHeight) || 1;
  const padX = width * 0.08;
  const padBottom = height * 0.07;
  const maxW = Math.max(1, width - padX * 2);
  const maxH = Math.max(1, height - padBottom * 1.2);
  const scale = Math.min(maxW / srcW, maxH / srcH);
  const w = srcW * scale;
  const h = srcH * scale;
  return {
    scaleX: scale,
    scaleY: scale,
    left: (width - w) / 2,
    top: height - h - padBottom,
  };
}

/**
 * Build a print-ready inner neck-label SVG. Size letter is the preview size
 * (per-variant substitution stays on the saved config). Content is packed
 * toward the bottom of the print box so it sits at the collar.
 */
export function buildNeckLabelSvg(content, config = {}) {
  const merged = { ...DEFAULT_NECK_LABEL_CONFIG, ...config };
  const style = getSizeFontStyle(merged.sizeFontStyle);
  const color = escapeXml(
    resolveNeckLabelTextColor(merged, config.garmentHex),
  );
  const rawSize = coerceSizeLabel(content?.previewSize || content?.sizes?.[0]);
  const size = escapeXml(rawSize);
  const careText = content?.careText || "";
  const careSteps = Array.isArray(content?.careSteps) ? content.careSteps : [];
  const flags = careFlagsFromConfig(config);
  const showSymbols = flags.careSymbols && careSteps.length > 0;
  const wrappedCare = flags.careWritten
    ? wrapLabelText(careText, 36, 3).split("\n").filter(Boolean)
    : [];

  const metaLines = [content?.fabric, content?.origin]
    .filter(Boolean)
    .flatMap((line) => wrapLabelText(line, 34, 2).split("\n").filter(Boolean));

  const sizeFont =
    rawSize.length > 3
      ? Math.round(style.fontSize * 0.58)
      : rawSize.length > 2
        ? Math.round(style.fontSize * 0.78)
        : style.fontSize;
  const sizeDescent = size ? Math.round(sizeFont * (style.descent || 0)) : 0;
  const sizeBlock = size ? sizeFont + 12 + sizeDescent : 0;
  const metaBlock = metaLines.length * 32;
  const symbolBlock = showSymbols ? 56 : 0;
  const writtenBlock = wrappedCare.length * 28;
  const careBlock =
    symbolBlock + (symbolBlock && writtenBlock ? 8 : 0) + writtenBlock;
  const stack = sizeBlock + metaBlock + (metaBlock && careBlock ? 8 : 0) + careBlock;
  // Lay the stack out from a start baseline, then measure where the ink ends
  // so the care copy can be pinned to the bottom of the label.
  const layout = (startY) => {
    let y = startY;
    let bottom = y;

    const sizeMarkup = size
      ? `<text x="360" y="${y}" text-anchor="middle" font-family="${escapeXml(style.fontFamily)}" font-weight="${style.fontWeight}" font-size="${sizeFont}" letter-spacing="${style.letterSpacing}"${style.numeric ? ` style="font-variant-numeric: ${style.numeric}"` : ""} fill="${color}">${size}</text>`
      : "";
    if (size) {
      bottom = y + sizeDescent;
      y += 28 + sizeDescent;
    }

    const metaMarkup = metaLines
      .map((line) => {
        const node = `<text x="360" y="${y}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="22" fill="${color}">${escapeXml(line)}</text>`;
        bottom = y + 6;
        y += 32;
        return node;
      })
      .join("");

    if (metaBlock && careBlock) y += 6;
    let careMarkup = "";
    if (showSymbols) {
      const stepCount = Math.min(careSteps.length, 6);
      const totalWidth = stepCount * 56;
      const startX = 360 - totalWidth / 2;
      careMarkup = careSteps
        .slice(0, 6)
        .map((step, index) =>
          careSymbolMarkup(step.icon, startX + index * 56, y - 8, color),
        )
        .join("");
      bottom = y + 40;
      y += 56;
    }
    if (wrappedCare.length) {
      if (showSymbols) y += 4;
      careMarkup += wrappedCare
        .map((line, index) => {
          return `<text x="360" y="${y + index * 28}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="20" fill="${color}">${escapeXml(line)}</text>`;
        })
        .join("");
      bottom = y + (wrappedCare.length - 1) * 28 + 6;
    }
    return { markup: `${sizeMarkup}
  ${metaMarkup}
  ${careMarkup}`, bottom };
  };

  // Pin the care copy (the last ink) to the bottom of the label; the size
  // and fabric/origin sit directly above it, leaving the top for the logo.
  const firstY = Math.max(72, 456 - stack + (size ? sizeFont * 0.78 : 0));
  const draft = layout(firstY);
  const drop = Math.max(0, NECK_LABEL_CONTENT_BOTTOM - draft.bottom);
  const { markup } = drop ? layout(firstY + drop) : draft;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="720" height="480" viewBox="0 0 720 480" overflow="hidden">
  <rect width="720" height="480" fill="none"/>
  ${markup}
</svg>`;
}

export function buildNeckLabelSeed(basePlacement = {}) {
  return {
    ...basePlacement,
    position: NECK_LABEL_PLACEMENT_ID,
    selectedObjects: basePlacement.selectedObjects || {},
    neckLabel: {
      ...DEFAULT_NECK_LABEL_CONFIG,
      ...(basePlacement.neckLabel || {}),
      mode: "inner",
    },
  };
}

export function findNeckLabelConfig(placementDetails, colorKey = "default") {
  return (
    placementDetails?.[colorKey]?.[NECK_LABEL_PLACEMENT_ID]?.neckLabel ||
    placementDetails?.default?.[NECK_LABEL_PLACEMENT_ID]?.neckLabel ||
    null
  );
}

export function placementHasSavedNeckLabel(placement) {
  return !!(placement?.neckLabel && typeof placement.neckLabel === "object");
}

export function treeHasSavedNeckLabel(tree) {
  return Object.values(tree || {}).some((byPlc) =>
    Object.values(byPlc || {}).some((placement) =>
      placementHasSavedNeckLabel(placement),
    ),
  );
}

/** Bulk merch-drop neck label → editor config. */
export function editorNeckLabelFromMerchDrop(merch) {
  const raw = merch?.neckLabel;
  const sizeUrls = merch?.sizeDesignUrls || {};
  const files = raw?.filesBySize || {};
  const sizeArt = {};
  Object.entries(files).forEach(([size, file]) => {
    if (!file?.url) return;
    sizeArt[String(size)] = {
      id: file.id || "",
      url: String(file.url),
      name: file.name || size,
    };
  });
  Object.entries(sizeUrls).forEach(([size, url]) => {
    if (!url || sizeArt[size]) return;
    sizeArt[String(size)] = { url: String(url), name: String(size) };
  });
  if (!raw && !Object.keys(sizeArt).length) return null;
  const uploaded = raw?.mode === "bring" || Object.keys(sizeArt).length > 0;
  return {
    ...DEFAULT_NECK_LABEL_CONFIG,
    mode: "inner",
    artworkSource: uploaded ? "uploaded" : "generated",
    sizeFontStyle: raw?.font || "block",
    careLayout: raw?.care || raw?.careLayout || "both",
    careSymbols: raw?.careSymbols !== false,
    careWritten: raw?.careWritten !== false,
    textColor: raw?.autoColor === false ? "#111111" : "auto",
    logoSrc: raw?.brand?.url || "",
    logoName: raw?.brand?.name || "",
    sizeArt,
  };
}

export function seedNeckLabelOnTree(tree, config) {
  if (!config) return tree || {};
  const next = { ...(tree || {}) };
  const colorKeys = Object.keys(next).length ? Object.keys(next) : ["default"];
  if (!colorKeys.includes("default")) colorKeys.unshift("default");
  colorKeys.forEach((color) => {
    const byPlc = { ...(next[color] || {}) };
    const existing = byPlc[NECK_LABEL_PLACEMENT_ID] || {};
    if (!placementHasSavedNeckLabel(existing)) {
      byPlc[NECK_LABEL_PLACEMENT_ID] = buildNeckLabelSeed({
        ...existing,
        neckLabel: config,
      });
    }
    next[color] = byPlc;
  });
  return next;
}

export function buildNeckLabelSvgsBySize(content, config, sizes) {
  const list = (sizes || content?.sizes || []).filter(Boolean);
  return list.reduce((acc, size) => {
    acc[size] = buildNeckLabelSvg({ ...content, previewSize: size }, config);
    return acc;
  }, {});
}

export function mergeCanvasImagesByVariant(base = {}, extra = {}) {
  const out = { ...base };
  Object.entries(extra || {}).forEach(([size, colors]) => {
    out[size] = { ...(out[size] || {}) };
    Object.entries(colors || {}).forEach(([color, placements]) => {
      out[size][color] = {
        ...(out[size][color] || {}),
        ...placements,
      };
    });
  });
  return out;
}

// Keep aliases here (not imported from listings.js) to avoid a circular import.
const SIZE_ALIASES = {
  small: "s",
  sml: "s",
  medium: "m",
  med: "m",
  large: "l",
  lrg: "l",
  "extra large": "xl",
  "x-large": "xl",
  xlarge: "xl",
  xlg: "xl",
  "2x": "2xl",
  xxl: "2xl",
  "2x-large": "2xl",
  "3x": "3xl",
  xxxl: "3xl",
  "3x-large": "3xl",
  "4x": "4xl",
  "5x": "5xl",
};

export const normalizeSizeKey = (size) => {
  const s = String(size || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return SIZE_ALIASES[s] || s.replace(/\s+/g, "");
};

export const sizesMatchLoose = (a, b) =>
  normalizeSizeKey(a) === normalizeSizeKey(b);

function pickSizeBucket(canvasImagesByVariant = {}, size) {
  if (!size) return null;
  if (canvasImagesByVariant[size]) return canvasImagesByVariant[size];
  const matchKey = Object.keys(canvasImagesByVariant).find((key) =>
    sizesMatchLoose(key, size),
  );
  return matchKey ? canvasImagesByVariant[matchKey] : null;
}

function pickColorBucket(sizeBucket = {}, color) {
  if (color && sizeBucket[color]) return sizeBucket[color];
  if (sizeBucket.default) return sizeBucket.default;
  if (!color) return {};
  const matchKey = Object.keys(sizeBucket).find(
    (key) =>
      String(key || "").trim().toLowerCase() ===
      String(color).trim().toLowerCase(),
  );
  return matchKey ? sizeBucket[matchKey] : {};
}

function pickColorImages(canvasImages = {}, color) {
  if (color && canvasImages[color]) return canvasImages[color];
  if (canvasImages.default) return canvasImages.default;
  if (!color) return {};
  const matchKey = Object.keys(canvasImages).find(
    (key) =>
      String(key || "").trim().toLowerCase() ===
      String(color).trim().toLowerCase(),
  );
  return matchKey ? canvasImages[matchKey] : {};
}

/**
 * Color-level print files, with placement 5 swapped for the size-specific
 * neck label when one was generated. Used by listing mapping + existing
 * listing re-saves so S/M/L do not share one baked letter.
 */
export function resolvePlacementImagesForVariant({
  canvasImages = {},
  canvasImagesByVariant = {},
  color,
  size,
} = {}) {
  const colorImages = pickColorImages(canvasImages, color);
  const sizeBucket = pickSizeBucket(canvasImagesByVariant, size) || {};
  const sizeImages = pickColorBucket(sizeBucket, color);
  const placementIds = [
    ...new Set([
      ...Object.keys(colorImages || {}),
      ...Object.keys(sizeImages || {}),
    ]),
  ];
  return placementIds.map((plcmntId) => ({
    placement: plcmntId,
    ...(colorImages[plcmntId] || {}),
    ...(sizeImages[plcmntId] || {}),
  }));
}

function fileUrl(file) {
  if (!file || typeof file !== "object") return "";
  return file.url || file.printFile?.url || file.storageInfo?.publicUrl || "";
}

/**
 * The neck-label file for this size. The color-level canvas is only the
 * example size (often Small), so callers must not fall back to it.
 */
export function neckLabelArtForVariant({
  canvasImagesByVariant = {},
  placementDetails,
  color,
  size,
} = {}) {
  if (!size) return null;
  const sized = resolvePlacementImagesForVariant({
    canvasImages: {},
    canvasImagesByVariant,
    color,
    size,
  }).find(
    (img) =>
      String(img.placement) === NECK_LABEL_PLACEMENT_ID && fileUrl(img)
  );
  if (sized) {
    const url = fileUrl(sized);
    return {
      placement: NECK_LABEL_PLACEMENT_ID,
      url,
      name: sized.name || sized.printFile?.name || "",
      printFile: sized.printFile?.url ? sized.printFile : { url, name: sized.name },
    };
  }

  const config =
    findNeckLabelConfig(placementDetails, color) ||
    findNeckLabelConfig(placementDetails, "default");
  const sizeArt = config?.sizeArt;
  if (!sizeArt || typeof sizeArt !== "object") return null;
  const key =
    (sizeArt[size] && size) ||
    Object.keys(sizeArt).find((candidate) => sizesMatchLoose(candidate, size));
  const art = key ? sizeArt[key] : null;
  const url = fileUrl(art);
  if (!url) return null;
  return {
    placement: NECK_LABEL_PLACEMENT_ID,
    url,
    name: art.name || art.printFile?.name || "",
    printFile: art.printFile?.url ? art.printFile : { url, name: art.name },
  };
}

export function isNeckLabelImage(image) {
  const placement = String(image?.placementId ?? image?.placement ?? "").trim();
  if (placement === NECK_LABEL_PLACEMENT_ID) return true;
  return /^neck(\s|-)?(label|tag)?$/i.test(placement);
}

/** Point the neck slot at this size's file. Other placements stay put. */
export function preferNeckLabelArt(images, art) {
  const list = Array.isArray(images) ? images.slice() : [];
  if (!art?.url) return list;
  const index = list.findIndex((image) => isNeckLabelImage(image));
  const current = index >= 0 ? list[index] : {};
  const next = {
    ...current,
    placement: current.placement ?? NECK_LABEL_PLACEMENT_ID,
    placementId: current.placementId || NECK_LABEL_PLACEMENT_ID,
    url: art.url,
    name: art.name || current.name,
    printFile: art.printFile || { url: art.url, name: art.name },
    printFileUrl: art.url,
  };
  if (index >= 0) list[index] = next;
  else {
    list.push({
      ...next,
      key: "neck-size",
      placement: NECK_LABEL_PLACEMENT_ID,
    });
  }
  return list;
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // data: and blob: URLs fail in Chrome and Safari when crossOrigin is set.
    if (!/^(data:|blob:)/i.test(String(src || ""))) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load neck label image"));
    img.src = src;
  });
}

/**
 * Box the uploaded logo may fill, in art units: from the top padding down to
 * just above the size letter (the care copy stays pinned at the bottom).
 */
export function getNeckLabelLogoBox(content, config = {}) {
  const preview = buildNeckLabelSvg(content, config);
  const match = preview.match(
    /<text x="360" y="([\d.]+)"[^>]*?font-size="([\d.]+)"/,
  );
  const baseline = Number(match?.[1]) || 200;
  const font = Number(match?.[2]) || 0;
  const inkTop = baseline - font * 0.78;
  const top = NECK_LABEL_LOGO_PAD.top;
  const bottom = Math.max(top + 24, inkTop - NECK_LABEL_LOGO_PAD.gap);
  return {
    top,
    height: bottom - top,
    maxWidth: NECK_LABEL_ART_SIZE.width - NECK_LABEL_LOGO_PAD.side * 2,
  };
}

/**
 * Scale an image to fill the logo box (up or down), centered horizontally
 * and sitting right above the size. Returns art-unit coordinates.
 */
export function fitNeckLabelLogo(box, imageWidth, imageHeight) {
  const w = Number(imageWidth) || 1;
  const h = Number(imageHeight) || 1;
  const scale = Math.min(box.maxWidth / w, box.height / h);
  const width = w * scale;
  const height = h * scale;
  return {
    scale,
    width,
    height,
    left: (NECK_LABEL_ART_SIZE.width - width) / 2,
    top: box.top + (box.height - height),
  };
}

export async function paintNeckLabelLogo(ctx, logoSrc, canvasWidth, box) {
  if (!logoSrc || !ctx || !box) return;
  const img = await loadHtmlImage(logoSrc);
  const toCanvas = canvasWidth / NECK_LABEL_ART_SIZE.width;
  const fit = fitNeckLabelLogo(box, img.width, img.height);
  ctx.drawImage(
    img,
    fit.left * toCanvas,
    fit.top * toCanvas,
    fit.width * toCanvas,
    fit.height * toCanvas,
  );
}

/**
 * Two studio layers for a generated label that has an uploaded mark: the
 * image, and the size/care copy under it. Positions match the single
 * bottom-pinned label, using the same logo fit as paintNeckLabelLogo.
 * Returns null when there is no logo (keep one image).
 */
export function neckLabelStudioPieces({
  canvasWidth,
  canvasHeight,
  artWidth,
  artHeight,
  careTrim,
  logo,
  logoBox,
} = {}) {
  if (!logo?.src || !(logo.width > 0) || !(logo.height > 0)) return null;
  if (!careTrim?.blob || !(careTrim.width > 0) || !(careTrim.height > 0)) {
    return null;
  }
  const artW = Number(artWidth) || 0;
  const artH = Number(artHeight) || 0;
  if (!(artW > 0) || !(artH > 0)) return null;
  const board = neckLabelCanvasLayout({
    canvasWidth,
    canvasHeight,
    imageWidth: artW,
    imageHeight: artH,
  });
  const scale = board.scaleX;
  if (!(scale > 0)) return null;
  if (!logoBox) return null;
  const toArt = artW / NECK_LABEL_ART_SIZE.width;
  const fit = fitNeckLabelLogo(logoBox, logo.width, logo.height);
  const logoScale = fit.scale * toArt;
  return {
    care: {
      name: "Size and care.png",
      blob: careTrim.blob,
      frame: {
        left: board.left + careTrim.x * scale,
        top: board.top + careTrim.y * scale,
        scaleX: scale,
        scaleY: scale,
      },
    },
    logo: {
      name: logo.name || "Neck image.png",
      src: logo.src,
      width: logo.width,
      height: logo.height,
      blob: logo.blob,
      frame: {
        left: board.left + fit.left * toArt * scale,
        top: board.top + fit.top * toArt * scale,
        scaleX: logoScale * scale,
        scaleY: logoScale * scale,
      },
    },
  };
}

/** Crop a transparent PNG to its ink so the care layer's box hugs the text. */
export async function trimPngBlob(blob, padding = 2) {
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadHtmlImage(url);
    const width = img.width || img.naturalWidth || 0;
    const height = img.height || img.naturalHeight || 0;
    if (!width || !height) return null;
    const source = document.createElement("canvas");
    source.width = width;
    source.height = height;
    const ctx = source.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] <= 8) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return null;
    const pad = Math.max(0, padding);
    const x = Math.max(0, minX - pad);
    const y = Math.max(0, minY - pad);
    const right = Math.min(width - 1, maxX + pad);
    const bottom = Math.min(height - 1, maxY + pad);
    const cropW = right - x + 1;
    const cropH = bottom - y + 1;
    const out = document.createElement("canvas");
    out.width = cropW;
    out.height = cropH;
    out.getContext("2d").drawImage(source, x, y, cropW, cropH, 0, 0, cropW, cropH);
    const trimmed = await new Promise((resolve, reject) => {
      out.toBlob(
        (next) => (next ? resolve(next) : reject(new Error("empty neck label"))),
        "image/png",
      );
    });
    return {
      blob: trimmed,
      x,
      y,
      width: cropW,
      height: cropH,
      sourceWidth: width,
      sourceHeight: height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * When the merchant uploaded a mark, return the care text and that image as
 * separate canvas layers. Print-file generation still composites them.
 */
export async function splitGeneratedNeckLabelLayers({
  content,
  config,
  svgToPng,
  rasterWidth,
  rasterHeight,
  canvasWidth,
  canvasHeight,
} = {}) {
  if (!config?.logoSrc) return null;
  try {
    const separated = await rasterizeNeckLabel({
      content,
      config,
      svgToPng,
      width: rasterWidth,
      height: rasterHeight,
      includeLogo: false,
    });
    if (!separated?.blob) return null;
    const careTrim = await trimPngBlob(separated.blob);
    if (!careTrim) return null;
    const img = await loadHtmlImage(config.logoSrc);
    const width = img.width || img.naturalWidth || 0;
    const height = img.height || img.naturalHeight || 0;
    if (!width || !height) return null;
    const logoRes = await fetch(config.logoSrc);
    if (!logoRes.ok) return null;
    const logoBlob = await logoRes.blob();
    return neckLabelStudioPieces({
      canvasWidth,
      canvasHeight,
      artWidth: rasterWidth,
      artHeight: rasterHeight,
      careTrim,
      logo: {
        src: config.logoSrc,
        name: config.logoName || "Neck image.png",
        width,
        height,
        blob: logoBlob,
      },
      logoBox: getNeckLabelLogoBox(content, config),
    });
  } catch (error) {
    return null;
  }
}

/**
 * Rasterize the inner label. Print files paint the logo into that bitmap.
 * The studio passes includeLogo: false and places the mark as its own layer.
 * Nested <image> tags inside an SVG-as-image are dropped by the browser.
 */
export async function rasterizeNeckLabel({
  content,
  config,
  svgToPng,
  width = NECK_LABEL_ART_SIZE.width,
  height = NECK_LABEL_ART_SIZE.height,
  includeLogo = true,
} = {}) {
  const svg = buildNeckLabelSvg(content, config);
  const blob = await svgToPng(svg, width, height);
  if (!includeLogo || !config?.logoSrc) return { svg, blob };

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const textUrl = URL.createObjectURL(blob);
  try {
    const textImg = await loadHtmlImage(textUrl);
    ctx.drawImage(textImg, 0, 0, width, height);
    await paintNeckLabelLogo(
      ctx,
      config.logoSrc,
      width,
      getNeckLabelLogoBox(content, config),
    );
  } finally {
    URL.revokeObjectURL(textUrl);
  }

  const composed = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (next) => (next ? resolve(next) : reject(new Error("empty neck label"))),
      "image/png",
    );
  });
  return { svg, blob: composed };
}

export async function readNeckLabelLogoFile(file) {
  if (!file) return { logoSrc: "", logoName: "" };
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Couldn’t read that image"));
    reader.readAsDataURL(file);
  });
  const img = await loadHtmlImage(dataUrl);
  const max = 900;
  const scale = Math.min(1, max / Math.max(img.width || 1, img.height || 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round((img.width || 1) * scale));
  canvas.height = Math.max(1, Math.round((img.height || 1) * scale));
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return {
    logoSrc: canvas.toDataURL("image/png"),
    logoName: file.name || "logo.png",
  };
}

export function normalizeNeckLabelConfig(config) {
  const flags = careFlagsFromConfig(config || {});
  const merged = {
    ...DEFAULT_NECK_LABEL_CONFIG,
    ...(config || {}),
    mode: "inner",
    ...flags,
    careLayout: careLayoutFromFlags(flags.careSymbols, flags.careWritten),
  };
  return {
    ...merged,
    artworkSource: merged.artworkSource === "uploaded" ? "uploaded" : "generated",
    sizeArt:
      merged.sizeArt && typeof merged.sizeArt === "object"
        ? { ...merged.sizeArt }
        : {},
    unmatchedSizeArt: Array.isArray(merged.unmatchedSizeArt)
      ? [...merged.unmatchedSizeArt]
      : [],
    excludedSizes: uniqueExcludedSizes(merged.excludedSizes),
  };
}

function uniqueExcludedSizes(sizes) {
  const out = [];
  (Array.isArray(sizes) ? sizes : []).forEach((size) => {
    const label = String(size || "").trim();
    if (!label) return;
    if (out.some((item) => sizesMatchLoose(item, label))) return;
    out.push(label);
  });
  return out;
}

export function neckLabelSizeExcluded(config, size) {
  if (config?.artworkSource !== "uploaded") return false;
  return uniqueExcludedSizes(config?.excludedSizes).some((item) =>
    sizesMatchLoose(item, size),
  );
}

/** Drop a size from the neck-label set, or put it back. */
export function setNeckLabelSizeIncluded(config, size, included) {
  const current = normalizeNeckLabelConfig(config);
  const excluded = uniqueExcludedSizes(current.excludedSizes).filter(
    (item) => !sizesMatchLoose(item, size),
  );
  if (!included) excluded.push(size);
  let files = neckLabelArtFiles(current);
  if (!included) {
    files = files.filter((file) => !sizesMatchLoose(file.size, size));
  }
  return neckLabelConfigFromArtFiles(
    { ...current, excludedSizes: excluded, artworkSource: "uploaded" },
    files,
  );
}

/**
 * Sizes the merchant turned off for uploaded neck labels. Those sizes have
 * no label, so they cannot be sold.
 */
export function neckLabelSizesExcludedFromSale(placementDetails) {
  const excluded = [];
  let uploaded = false;
  Object.values(placementDetails || {}).forEach((placements) => {
    if (!placements || typeof placements !== "object") return;
    const neck =
      placements?.[NECK_LABEL_PLACEMENT_ID]?.neckLabel ||
      placements?.[Number(NECK_LABEL_PLACEMENT_ID)]?.neckLabel;
    if (neck?.artworkSource !== "uploaded") return;
    uploaded = true;
    uniqueExcludedSizes(neck.excludedSizes).forEach((size) => {
      if (!excluded.some((item) => sizesMatchLoose(item, size))) {
        excluded.push(size);
      }
    });
  });
  return uploaded ? excluded : [];
}

export function sizeExcludedFromNeckLabel(placementDetails, size) {
  const label = coerceSizeLabel(size);
  return neckLabelSizesExcludedFromSale(placementDetails).some((item) =>
    sizesMatchLoose(item, label),
  );
}

function rankCode(raw) {
  const key = String(raw || "")
    .toUpperCase()
    .replace(/[_./]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!key) return "";
  for (const group of SIZE_RANK_ALIASES) {
    if (group.includes(key)) return group[0];
  }
  const compact = key.replace(/[-\s]/g, "");
  for (const group of SIZE_RANK_ALIASES) {
    if (group.some((alias) => alias.replace(/[-\s]/g, "") === compact)) {
      return group[0];
    }
  }
  return "";
}

function youthCode(size) {
  const compact = String(size || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (YOUTH_ALIASES[compact]) return compact;
  const spaced = String(size || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
  const match = spaced.match(/^YOUTH\s+(.+)$/);
  if (!match) return "";
  const rank = rankCode(match[1]);
  return { XS: "YXS", S: "YS", M: "YM", L: "YL", XL: "YXL" }[rank] || "";
}

function tokensForSize(size) {
  const raw = String(size || "").trim();
  const tokens = new Set();
  const add = (value) => {
    const text = String(value || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    if (text) tokens.add(text);
  };
  add(raw);
  const rank = rankCode(raw);
  if (rank) {
    const group = SIZE_RANK_ALIASES.find((item) => item[0] === rank) || [];
    group.forEach(add);
  }
  const youth = youthCode(raw);
  if (youth) (YOUTH_ALIASES[youth] || []).forEach(add);
  return [...tokens].sort((a, b) => b.length - a.length);
}

function tokenMatches(haystack, token) {
  const text = String(token || "")
    .toLowerCase()
    .trim();
  if (!text) return false;
  const body = text
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/[-_\s]+/g, "[^a-z0-9]*");
  const re = new RegExp(`(^|[^a-z0-9])${body}([^a-z0-9]|$)`, "i");
  return re.test(String(haystack || ""));
}

/**
 * One spelling per size: "Medium" → "M", "XXL" → "2XL", "Youth Small" → "YS".
 * Neck-label sets match product sizes through this. The Node API's
 * functions/neckLabels/sizes.js canonicalSize mirrors it — keep them in step.
 */
export function canonicalNeckSize(size) {
  const text = String(size == null ? "" : size).trim();
  if (!text) return "";
  return youthCode(text) || rankCode(text) || text.toUpperCase().replace(/\s+/g, "");
}

export const neckSizesMatch = (a, b) => {
  const left = canonicalNeckSize(a);
  return Boolean(left) && left === canonicalNeckSize(b);
};

/**
 * Pick the catalog size named by a file, using the longest size token.
 * "label-2XL.png" matches 2XL, not XL. Ambiguous names return "".
 */
export function matchNeckLabelFileToSize(fileName, sizes) {
  const stem = String(fileName || "").replace(/\.[^.]+$/, "");
  let bestLen = 0;
  const hits = [];
  (sizes || []).forEach((size) => {
    const token = tokensForSize(size).find((item) => tokenMatches(stem, item));
    if (!token) return;
    if (token.length > bestLen) {
      bestLen = token.length;
      hits.length = 0;
      hits.push(size);
    } else if (token.length === bestLen) {
      hits.push(size);
    }
  });
  return hits.length === 1 ? hits[0] : "";
}

/**
 * Assign uploaded files to sizes. A size already taken (or a second file
 * for the same size) stays unmatched so it can be mapped by hand.
 */
export function assignNeckLabelFiles(files, sizes, takenSizes = []) {
  const used = new Set((takenSizes || []).filter(Boolean));
  const mapped = {};
  const unmatched = [];
  (files || []).forEach((file) => {
    const size = matchNeckLabelFileToSize(file?.name, sizes);
    if (size && !used.has(size)) {
      used.add(size);
      mapped[size] = file;
      return;
    }
    unmatched.push(file);
  });
  return { mapped, unmatched };
}

export function neckLabelArtFiles(config) {
  const files = [];
  Object.entries(config?.sizeArt || {}).forEach(([size, art]) => {
    if (!art?.src && !art?.url) return;
    files.push({
      id: art.id || `size:${size}`,
      name: art.name || `${size}.png`,
      src: art.src || "",
      url: art.url || "",
      printFile: art.printFile,
      size,
    });
  });
  (config?.unmatchedSizeArt || []).forEach((art, index) => {
    if (!art?.src && !art?.url) return;
    files.push({
      id: art.id || `unmatched:${index}`,
      name: art.name || "label.png",
      src: art.src || "",
      url: art.url || "",
      printFile: art.printFile,
      size: "",
    });
  });
  return files;
}

export function neckLabelConfigFromArtFiles(config, files) {
  const sizeArt = {};
  const unmatchedSizeArt = [];
  (files || []).forEach((file) => {
    const art = {
      id: file.id,
      name: file.name,
      ...(file.src ? { src: file.src } : {}),
      ...(file.url ? { url: file.url } : {}),
      ...(file.printFile ? { printFile: file.printFile } : {}),
    };
    if (file.size) sizeArt[file.size] = art;
    else unmatchedSizeArt.push(art);
  });
  return normalizeNeckLabelConfig({
    ...config,
    artworkSource: "uploaded",
    sizeArt,
    unmatchedSizeArt,
  });
}

export function assignNeckLabelArtFile(files, fileId, size) {
  const nextSize = size || "";
  return (files || []).map((file) => {
    if (file.id === fileId) return { ...file, size: nextSize };
    if (nextSize && file.size === nextSize) return { ...file, size: "" };
    return file;
  });
}

export function removeNeckLabelArtFile(files, fileId) {
  return (files || []).filter((file) => file.id !== fileId);
}

/**
 * Sizes the product is actually selling: rows checked in the pricing table.
 * Checkbox state is kept by row position (`selectedItems[index]`), opt-out
 * style — a row is on unless it is `false`. Before the table has set any
 * state, the product's saved size/color keys decide (same key as
 * `variationSelectionKey` in helpers/listings, kept here to avoid an import
 * cycle). Unchecked sizes are not sold, so they never need a neck label.
 */
export function sizesOnForSale(rows = [], selectedItems = {}, savedKeys = []) {
  const saved = new Set(savedKeys || []);
  const keyOf = (row = {}) =>
    `${String(row.size || row.variant?.size || "")}::${String(
      row.color || row.variation || row.variant?.color || "",
    )}`;
  const on = (Array.isArray(rows) ? rows : []).filter((row, index) => {
    const picked = selectedItems?.[index];
    if (picked !== undefined) return picked !== false;
    if (row?.selected !== undefined) return !!row.selected;
    return saved.size > 0 ? saved.has(keyOf(row)) : true;
  });
  return uniqueSizeLabels(on.map((row) => row?.size || row?.variant?.size));
}

/** Which uploaded labels still need a size, and which included sizes still need a label. */
export function uploadedNeckLabelStatus(config, sizes = []) {
  const list = uniqueSizeLabels(sizes);
  const required = list.filter((size) => !neckLabelSizeExcluded(config, size));
  const files = neckLabelArtFiles(config);
  const missing = required.filter(
    (size) => !files.some((file) => sizesMatchLoose(file.size, size)),
  );
  const unmatched = files.filter((file) => !file.size);
  const ready =
    config?.artworkSource === "uploaded" &&
    required.length > 0 &&
    missing.length === 0 &&
    unmatched.length === 0;
  return { missing, unmatched, ready, required };
}

export function uploadedNeckLabelBlockMessage(status, action = "applying") {
  if (!status || status.ready) return "";
  const parts = [];
  if (!status.missing?.length && !status.unmatched?.length && !status.required?.length) {
    return `Choose at least one size before ${action}.`;
  }
  const missingLabels = uniqueSizeLabels(status.missing);
  if (missingLabels.length) {
    parts.push(`Add a label for ${missingLabels.join(", ")}`);
  }
  if (status.unmatched?.length) {
    const names = status.unmatched.map((file) => file.name).filter(Boolean);
    parts.push(
      names.length
        ? `Choose a size for ${names.join(", ")}`
        : "Choose a size for every file",
    );
  }
  if (!parts.length) return `Add a label for every size before ${action}.`;
  return `${parts.join(". ")} before ${action}.`;
}

export function placementUploadedNeckLabelGaps(placementDetails, sizes = []) {
  const missing = new Set();
  const unmatched = [];
  let uploaded = false;
  let ready = true;
  Object.values(placementDetails || {}).forEach((placements) => {
    const neck =
      placements?.[NECK_LABEL_PLACEMENT_ID]?.neckLabel ||
      placements?.[Number(NECK_LABEL_PLACEMENT_ID)]?.neckLabel;
    if (neck?.artworkSource !== "uploaded") return;
    uploaded = true;
    const status = uploadedNeckLabelStatus(neck, sizes);
    if (!status.ready) ready = false;
    status.missing.forEach((size) => missing.add(size));
    unmatched.push(...status.unmatched);
  });
  return {
    uploaded,
    missing: [...missing],
    unmatched,
    ready: uploaded && ready,
  };
}

function blankNeckLabelSvg() {
  return `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="${NECK_LABEL_ART_SIZE.width}" height="${NECK_LABEL_ART_SIZE.height}" viewBox="0 0 ${NECK_LABEL_ART_SIZE.width} ${NECK_LABEL_ART_SIZE.height}"></svg>`;
}

/** Ink bounds of an RGBA buffer. Transparent padding is not part of the tag. */
export function opaquePixelBounds(data, width, height, alphaThreshold = 8) {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  const stride = width * 4;
  for (let y = 0; y < height; y += 1) {
    const row = y * stride;
    for (let x = 0; x < width; x += 1) {
      if (data[row + x * 4 + 3] <= alphaThreshold) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

/**
 * Where a layer sits after transparent margins are removed.
 * The printed ink stays put; only the empty sides leave the selection box.
 */
export function placedFrameAfterOpaqueCrop({
  left = 0,
  top = 0,
  scaleX = 1,
  scaleY = 1,
  originX = "left",
  originY = "top",
  sourceWidth,
  sourceHeight,
  cropX = 0,
  cropY = 0,
  cropWidth,
  cropHeight,
} = {}) {
  const shift = (origin, pos, nearPad, farPad, scale) => {
    if (origin === "center") return pos + ((nearPad - farPad) / 2) * scale;
    if (origin === "right" || origin === "bottom") return pos - farPad * scale;
    return pos + nearPad * scale;
  };
  const rightPad = (sourceWidth || 0) - cropX - (cropWidth || 0);
  const bottomPad = (sourceHeight || 0) - cropY - (cropHeight || 0);
  return {
    left: shift(originX, left, cropX, rightPad, scaleX || 1),
    top: shift(originY, top, cropY, bottomPad, scaleY || 1),
    scaleX: scaleX || 1,
    scaleY: scaleY || 1,
    width: cropWidth,
    height: cropHeight,
  };
}

function cropImageToOpaque(img) {
  const width = img?.naturalWidth || img?.width || 0;
  const height = img?.naturalHeight || img?.height || 0;
  if (!width || !height) return null;
  const source = document.createElement("canvas");
  source.width = width;
  source.height = height;
  const ctx = source.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  let data;
  try {
    data = ctx.getImageData(0, 0, width, height).data;
  } catch (error) {
    return { canvas: source, x: 0, y: 0, width, height, trimmed: false };
  }
  const bounds = opaquePixelBounds(data, width, height);
  if (!bounds) return null;
  if (
    bounds.x === 0 &&
    bounds.y === 0 &&
    bounds.width === width &&
    bounds.height === height
  ) {
    return { canvas: source, x: 0, y: 0, width, height, trimmed: false };
  }
  const out = document.createElement("canvas");
  out.width = bounds.width;
  out.height = bounds.height;
  out
    .getContext("2d")
    .drawImage(
      source,
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height,
      0,
      0,
      bounds.width,
      bounds.height,
    );
  return {
    canvas: out,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    trimmed: true,
  };
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("empty neck label"))),
      "image/png",
    );
  });
}

/**
 * Uploaded tags keep their own pixels. Letterboxing a square woven label onto
 * the 720×480 artboard added empty sides, and the customizer measured that
 * wider box against the print area.
 */
export async function readNeckLabelSizeArtFile(file) {
  if (!file) return null;
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Couldn’t read that image"));
    reader.readAsDataURL(file);
  });
  const img = await loadHtmlImage(dataUrl);
  const cropped = cropImageToOpaque(img);
  return {
    name: file.name || "label.png",
    src: cropped?.trimmed ? cropped.canvas.toDataURL("image/png") : dataUrl,
  };
}

export async function rasterizeUploadedNeckLabel(src) {
  if (!src) throw new Error("Missing neck label image");
  const img = await loadHtmlImage(src);
  const cropped = cropImageToOpaque(img);
  if (!cropped?.canvas) throw new Error("empty neck label");
  return canvasToPngBlob(cropped.canvas);
}

/**
 * Drop transparent side padding already baked into a placed uploaded tag.
 * Ink stays where it is; the selection box shrinks to the label.
 */
export function cropUploadedNeckLabelObjects(canvas, placement, onCropped) {
  if (!canvas?.getObjects || placement?.neckLabel?.artworkSource !== "uploaded") {
    return;
  }
  const selected = placement.selectedObjects || {};
  let pending = 0;
  canvas.getObjects().forEach((obj) => {
    if (!obj || obj.__neckLabelCropping || obj.type !== "image") return;
    const layer = selected[obj.objectId];
    if (!layer?.isNeckLabel) return;
    const el = obj._originalElement || obj._element;
    const sourceWidth = el?.naturalWidth || el?.width || 0;
    const sourceHeight = el?.naturalHeight || el?.height || 0;
    if (!sourceWidth || !sourceHeight) return;
    obj.__neckLabelCropping = true;
    const cropped = cropImageToOpaque(el);
    if (!cropped?.trimmed) return;
    const frame = placedFrameAfterOpaqueCrop({
      left: obj.left || 0,
      top: obj.top || 0,
      scaleX: obj.scaleX || 1,
      scaleY: obj.scaleY || 1,
      originX: obj.originX || "left",
      originY: obj.originY || "top",
      sourceWidth,
      sourceHeight,
      cropX: cropped.x,
      cropY: cropped.y,
      cropWidth: cropped.width,
      cropHeight: cropped.height,
    });
    pending += 1;
    obj.setSrc(cropped.canvas.toDataURL("image/png"), (image, isError) => {
      if (!isError && image) {
        image.set({
          left: frame.left,
          top: frame.top,
          scaleX: frame.scaleX,
          scaleY: frame.scaleY,
        });
        image.setCoords();
        canvas.fire?.("object:modified", { target: image });
      }
      pending -= 1;
      if (pending === 0) onCropped?.();
    });
  });
}

function previewUploadedNeckLabel(config, content) {
  const art = config?.sizeArt || {};
  const preferred = content?.previewSize;
  if (preferred && (art[preferred]?.src || art[preferred]?.url)) {
    return { ...art[preferred], size: preferred };
  }
  for (const size of content?.sizes || []) {
    if (art[size]?.src || art[size]?.url) return { ...art[size], size };
  }
  const first = Object.entries(art).find(([, value]) => value?.src || value?.url);
  if (!first) return null;
  return { ...first[1], size: first[0] };
}

/**
 * Image to show on the neck canvas for "Your labels": the preview size's
 * saved file (https), or "" when the config isn't using uploaded art or
 * nothing is saved yet. Picking a library set or reopening a product only
 * stores the config, so the designer paints this as a display-only layer.
 */
export function uploadedNeckLabelPreviewUrl(config, content) {
  if (config?.artworkSource !== "uploaded") return "";
  const art = previewUploadedNeckLabel(config, content);
  const url = art?.url || art?.printFile?.url || art?.src || "";
  return typeof url === "string" ? url : "";
}

async function persistArtEntry(art, uploadArt) {
  if (!art?.src && !art?.url) return null;
  if (art.url && !art.src) return art;
  if (!art.src || !uploadArt) {
    return {
      id: art.id,
      name: art.name,
      ...(art.src ? { src: art.src } : {}),
      ...(art.url ? { url: art.url } : {}),
      ...(art.printFile ? { printFile: art.printFile } : {}),
    };
  }
  const blob = await rasterizeUploadedNeckLabel(art.src);
  const file = new File([blob], art.name || "neck-label.png", {
    type: "image/png",
  });
  const uploaded = await uploadArt(file);
  if (!uploaded?.url) {
    return { id: art.id, name: art.name, src: art.src };
  }
  return {
    id: art.id || uploaded.id,
    name: art.name || uploaded.name || "neck-label.png",
    url: uploaded.url,
    printFile: uploaded,
  };
}

async function persistUploadedNeckLabelArt(config, uploadArt) {
  const sizeArt = {};
  for (const [size, art] of Object.entries(config.sizeArt || {})) {
    const next = await persistArtEntry(art, uploadArt);
    if (next) sizeArt[size] = next;
  }
  const unmatchedSizeArt = [];
  for (const art of config.unmatchedSizeArt || []) {
    const next = await persistArtEntry(art, uploadArt);
    if (next) unmatchedSizeArt.push(next);
  }
  return { ...config, sizeArt, unmatchedSizeArt };
}

/**
 * Generated labels rasterize legal copy. Uploaded labels keep the merchant
 * file for that size and store a print-file URL instead of the image bytes.
 */
export async function prepareNeckLabelArtwork({
  content,
  config,
  svgToPng,
  garmentHex,
  uploadArt,
} = {}) {
  const storedConfig = normalizeNeckLabelConfig(config);
  delete storedConfig.garmentHex;

  if (storedConfig.artworkSource === "uploaded") {
    const previewBefore = previewUploadedNeckLabel(storedConfig, content);
    let blob = null;
    const previewSource = previewBefore?.src || previewBefore?.url;
    if (previewSource) {
      try {
        blob = await rasterizeUploadedNeckLabel(previewSource);
      } catch (error) {
        blob = null;
      }
    }
    const persisted = await persistUploadedNeckLabelArt(storedConfig, uploadArt);
    const preview = previewUploadedNeckLabel(persisted, content);
    return {
      storedConfig: persisted,
      svg: blankNeckLabelSvg(),
      blob,
      previewUrl: preview?.url || preview?.src || "",
      name: preview?.name || "neck-label.png",
      missingPreview: !preview,
    };
  }

  const { svg, blob } = await rasterizeNeckLabel({
    content,
    config: { ...storedConfig, garmentHex },
    svgToPng,
  });
  return {
    storedConfig,
    svg,
    blob,
    previewUrl: "",
    name: "neck-label.png",
    missingPreview: false,
  };
}

export function neckLabelConfigForSave(config) {
  if (!config || config.artworkSource !== "uploaded") return config;
  const shrink = (art) => {
    if (!art) return art;
    if (!(art.url || art.printFile?.url)) return art;
    const next = { ...art };
    delete next.src;
    return next;
  };
  const sizeArt = {};
  Object.entries(config.sizeArt || {}).forEach(([size, art]) => {
    sizeArt[size] = shrink(art);
  });
  return {
    ...config,
    sizeArt,
    unmatchedSizeArt: (config.unmatchedSizeArt || []).map(shrink),
  };
}

export function placementDetailsForNeckLabelSave(placementDetails) {
  const next = {};
  Object.entries(placementDetails || {}).forEach(([color, placements]) => {
    next[color] = {};
    Object.entries(placements || {}).forEach(([id, placement]) => {
      if (!placement?.neckLabel) {
        next[color][id] = placement;
        return;
      }
      next[color][id] = {
        ...placement,
        neckLabel: neckLabelConfigForSave(placement.neckLabel),
      };
    });
  });
  return next;
}

function artPrintFile(art) {
  if (art?.printFile?.url) return art.printFile;
  if (art?.url && !art?.src) {
    return { id: art.id, url: art.url, name: art.name };
  }
  return null;
}

/** Print files already stored on an uploaded neck label, keyed for listing sync. */
export function uploadedNeckLabelVariantImages(placementDetails) {
  const out = {};
  Object.entries(placementDetails || {}).forEach(([color, placements]) => {
    const config = placements?.[NECK_LABEL_PLACEMENT_ID]?.neckLabel;
    if (config?.artworkSource !== "uploaded") return;
    const resolved = normalizeNeckLabelConfig(config);
    Object.entries(resolved.sizeArt || {}).forEach(([size, art]) => {
      if (neckLabelSizeExcluded(resolved, size)) return;
      const file = artPrintFile(art);
      if (!file?.url) return;
      out[size] = out[size] || {};
      out[size][color] = out[size][color] || {};
      out[size][color][NECK_LABEL_PLACEMENT_ID] = file;
    });
  });
  return out;
}


export async function buildNeckLabelFilesBySize({
  placementDetails,
  blankProduct,
  selectedSizes,
  svgToPng,
  blobToFile,
  garmentHexByColor = {},
} = {}) {
  const files = [];
  const colors = Object.keys(placementDetails || {});
  for (const color of colors) {
    const config = findNeckLabelConfig(placementDetails, color);
    if (!config) continue;
    const placement =
      placementDetails?.[color]?.[NECK_LABEL_PLACEMENT_ID] ||
      placementDetails?.default?.[NECK_LABEL_PLACEMENT_ID];
    const content = extractNeckLabelContent(blankProduct, { selectedSizes });
    const resolved = normalizeNeckLabelConfig({
      ...config,
      garmentHex: garmentHexByColor[color],
    });
    if (resolved.artworkSource === "uploaded") {
      const sizes = (
        content.sizes.length
          ? content.sizes
          : Object.keys(resolved.sizeArt || {})
      ).filter((size) => !neckLabelSizeExcluded(resolved, size));
      for (const size of sizes) {
        const art = resolved.sizeArt?.[size];
        const existingFile = artPrintFile(art);
        if (existingFile) {
          files.push({ size, color, existingFile, svg: "" });
          continue;
        }
        const source = art?.src || art?.url;
        if (!source) continue;
        const blob = await rasterizeUploadedNeckLabel(source);
        const file = blobToFile(
          blob,
          art?.name || `neck-label_${color}_${size}.png`,
        );
        files.push({ size, color, file, svg: "" });
      }
      continue;
    }
    // Config is seeded when they open the tab. Only emit print files after
    // Apply / a logo actually committed artwork.
    if (!placementHasRealArt(placement)) continue;
    if (placementHasManualNeckArt(placement)) continue;
    const sizes = content.sizes.length ? content.sizes : ["M"];
    for (const size of sizes) {
      const { svg, blob } = await rasterizeNeckLabel({
        content: { ...content, previewSize: size },
        config: resolved,
        svgToPng,
      });
      const file = blobToFile(blob, `neck-label_${color}_${size}.png`);
      files.push({ size, color, file, svg });
    }
  }
  return files;
}
